import CreditCardIcon from '@mui/icons-material/CreditCard';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';
import { signInAnonymously } from 'firebase/auth';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from 'AppRoutes/BeerStorePayment/styles';
import GrowlerPricingBreakdown from 'components/GrowlerPricingBreakdown';
import PaymentMethodTabs, {
  type PaymentMethodTabValue,
} from 'components/PaymentMethodTabs';
import { useGrowlersCartPricing } from 'hooks/useGrowlersCartPricing';
import { useGrowlersCashback } from 'hooks/useGrowlersCashback';
import { useAuth } from 'services/auth/authContext';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { auth } from 'services/firebase/client';
import {
  createBeerStorePayment,
  createOrderDocument,
} from 'services/paymentService';
import { formatCurrencyBRL, roundCurrency } from 'utils/currency';
import { GROWLER_DELIVERY_TIME } from 'utils/growlersFlow';
import {
  GROWLER_CUSTOMER_STORAGE_KEY,
  GROWLER_ORDER_STORAGE_KEY,
} from 'utils/types/GrowlerCustomer';

import GrowlerPixTab from './GrowlerPixTab';
import {
  buildGrowlerOrderItems,
  buildGrowlerOrderPayload,
  getGrowlerItemsForPayment,
  type GrowlerOrderData,
} from './growlerOrderUtils';

initMercadoPago('APP_USR-a95da5cf-9b11-4dba-b56d-061a1e0be09f');

const GrowlerPayment: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { cart, clearCart } = useGrowlerCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderData, setOrderData] = useState<GrowlerOrderData | null>(null);
  const [paymentTab, setPaymentTab] = useState<PaymentMethodTabValue>('card');
  const [applyCashback, setApplyCashback] = useState(true);
  const subtotal = roundCurrency(cart.totalValue);
  const pricing = useGrowlersCartPricing(cart.totalItems, subtotal);
  const { shippingCost, discount, total: pricingTotal } = pricing;
  const merchandiseSubtotal = roundCurrency(Math.max(0, subtotal - discount));
  const cashback = useGrowlersCashback(merchandiseSubtotal);
  const customerEmail = orderData?.customer?.email || '';
  const orderEmail = customerEmail.trim().toLowerCase();
  const emailMatchesAccount =
    Boolean(cashback.accountEmail) &&
    Boolean(orderEmail) &&
    cashback.accountEmail === orderEmail;
  const cashbackRedeemed =
    applyCashback && cashback.canRedeem && emailMatchesAccount
      ? cashback.maxRedeem
      : 0;
  const total = roundCurrency(Math.max(0, pricingTotal - cashbackRedeemed));

  useEffect(() => {
    const savedOrderData = localStorage.getItem(GROWLER_ORDER_STORAGE_KEY);

    if (savedOrderData) {
      try {
        setOrderData(JSON.parse(savedOrderData));
      } catch {
        navigate('/growlers/shipping');
        return;
      }
    } else {
      navigate('/growlers/shipping');
      return;
    }

    if (!cart.items.length) {
      navigate('/growlers');
    }
  }, [navigate, cart.items.length]);

  useEffect(() => {
    if (
      !pricing.loading &&
      !cashback.loading &&
      cart.items.length > 0 &&
      total <= 0
    ) {
      navigate('/growlers/error');
    }
  }, [
    cart.items.length,
    cashback.loading,
    navigate,
    pricing.loading,
    total,
  ]);

  const handleBack = () => {
    navigate('/growlers/shipping');
  };

  const loginHint = useMemo(() => {
    if (currentUser && !currentUser.isAnonymous) {
      return null;
    }
    return 'Faça login para usar seu cashback nesta compra.';
  }, [currentUser]);

  const handleCardSubmit = useCallback(
    async (param: any) => {
      let currentOrderData = orderData;
      if (!currentOrderData) {
        const savedOrderData = localStorage.getItem(GROWLER_ORDER_STORAGE_KEY);
        if (savedOrderData) {
          try {
            currentOrderData = JSON.parse(savedOrderData);
            setOrderData(currentOrderData);
          } catch {
            // Erro ao recarregar dados
          }
        }
      }

      setIsProcessing(true);

      try {
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }

        const payerEmail =
          currentOrderData?.customer?.email || param.payer?.email || '';

        const cartItemsForPayment = getGrowlerItemsForPayment(cart.items);

        const response = await createBeerStorePayment({
          token: param.token,
          payer: {
            email: payerEmail,
            identification: {
              type: 'CPF',
              number: param.payer.identification.number,
            },
          },
          amount: total,
          cartItems: cartItemsForPayment,
          isSubscriber: false,
        });

        if (!response || response.error || response.status !== 'approved') {
          navigate('/growlers/error');
          return;
        }

        try {
          if (!currentOrderData?.customer || !currentOrderData.shipping) {
            throw new Error('Dados do pedido não encontrados');
          }

          if (!auth.currentUser?.uid) {
            throw new Error('Usuário não autenticado');
          }

          const orderItems = buildGrowlerOrderItems(cart.items);
          const paymentId = response.payment_id || response.id;

          const orderPayload = buildGrowlerOrderPayload({
            userId: auth.currentUser.uid,
            userEmail: payerEmail,
            items: orderItems,
            subtotal,
            shippingCost,
            discount,
            cashbackRedeemed,
            total,
            orderData: currentOrderData,
            payment: {
              method: 'credit_card',
              status: 'approved',
              transactionId: paymentId,
              paymentId,
              amount: total,
            },
          });

          const cfOrder = await createOrderDocument(orderPayload);

          clearCart();
          localStorage.removeItem(GROWLER_ORDER_STORAGE_KEY);
          localStorage.removeItem(GROWLER_CUSTOMER_STORAGE_KEY);

          navigate('/growlers/success', {
            state: {
              orderId: cfOrder.id,
              orderNumber: cfOrder.orderNumber,
              total,
              items: cart.items,
              paymentId: response.id,
              cashbackCredited: cfOrder.cashbackCredited || 0,
              cashbackRedeemed: cfOrder.cashbackRedeemed || cashbackRedeemed,
              userEmail: cfOrder.userEmail || payerEmail,
              needsPasswordSetup: Boolean(cfOrder.needsPasswordSetup),
            },
          });
        } catch {
          clearCart();
          localStorage.removeItem(GROWLER_ORDER_STORAGE_KEY);
          localStorage.removeItem(GROWLER_CUSTOMER_STORAGE_KEY);

          navigate('/growlers/success', {
            state: {
              orderId: response.payment_id || response.id,
              orderNumber: `TEMP-${response.payment_id || response.id}`,
              total,
              items: cart.items,
              error:
                'Pagamento aprovado, mas houve erro ao salvar o pedido. Entre em contato conosco.',
            },
          });
        }
      } catch {
        navigate('/growlers/error');
      } finally {
        setIsProcessing(false);
      }
    },
    [
      cart.items,
      cashbackRedeemed,
      clearCart,
      discount,
      navigate,
      orderData,
      shippingCost,
      subtotal,
      total,
    ],
  );

  return (
    <Box sx={{ ...styles.container, px: { xs: 2, sm: 4 } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography
            variant="subtitle1"
            sx={{ ...styles.subtitle, textAlign: 'center', mb: 2 }}
          >
            FORMA DE PAGAMENTO
          </Typography>

          <PaymentMethodTabs
            value={paymentTab}
            onChange={setPaymentTab}
            showPix
          />

          {(pricing.loading || cashback.loading) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {paymentTab === 'card' && !pricing.loading && !cashback.loading && (
            <>
              <Card
                sx={{
                  ...styles.card,
                  mx: { xs: 1, sm: 2 },
                  px: { xs: 2, sm: 3 },
                }}
              >
                <CardContent sx={styles.paymentHeader}>
                  <CreditCardIcon sx={styles.icon} />
                  <Typography variant="body1">
                    Pagar com Cartão de Crédito
                  </Typography>
                </CardContent>
              </Card>

              <CardPayment
                key={`growler-card-${total}`}
                initialization={{
                  amount: total,
                  payer: { email: customerEmail },
                }}
                onSubmit={handleCardSubmit}
                locale="pt-BR"
                customization={{
                  paymentMethods: {
                    maxInstallments: 10,
                    types: {
                      included: ['credit_card'],
                    },
                  },
                }}
              />
            </>
          )}

          {paymentTab === 'pix' && !pricing.loading && !cashback.loading && (
            <GrowlerPixTab
              total={total}
              subtotal={subtotal}
              shippingCost={shippingCost}
              discount={discount}
              cashbackRedeemed={cashbackRedeemed}
              orderData={orderData}
              disabled={isProcessing}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={styles.subtitle}>
            RESUMO DO PEDIDO
          </Typography>
          <Card sx={{ ...styles.summaryCard, mx: { xs: 1, sm: 2 } }}>
            <CardContent>
              {cashback.canRedeem && emailMatchesAccount && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={applyCashback}
                      onChange={(_, checked) => setApplyCashback(checked)}
                      color="primary"
                    />
                  }
                  label={`Usar cashback (até R$ ${formatCurrencyBRL(
                    cashback.maxRedeem,
                  )})`}
                  sx={{ mb: 2 }}
                />
              )}

              {cashback.canRedeem && !emailMatchesAccount && orderEmail && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 2, color: 'text.secondary' }}
                >
                  O e-mail do pedido precisa ser o mesmo da conta logada para
                  usar o cashback.
                </Typography>
              )}

              {loginHint && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 2, color: 'text.secondary' }}
                >
                  {loginHint}
                </Typography>
              )}

              {cart.items.map(item => (
                <Box key={item.id} sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={styles.productText}>
                    {item.growler.name}
                  </Typography>
                  <Typography variant="body2" sx={styles.quantityText}>
                    Quantidade: {item.quantity} x R${' '}
                    {item.growler.price.toFixed(2).replace('.', ',')}
                  </Typography>
                  <Typography variant="body2" sx={styles.itemTotalText}>
                    Subtotal: R${' '}
                    {(item.growler.price * item.quantity)
                      .toFixed(2)
                      .replace('.', ',')}
                  </Typography>
                </Box>
              ))}

              <GrowlerPricingBreakdown
                pricing={{ ...pricing, total: pricingTotal }}
                cashbackRedeemed={cashbackRedeemed}
                cashbackEarnAmount={cashback.earnAmount}
                cashbackEarnPercent={cashback.earnPercent}
                payableTotal={total}
                rowSx={styles.summaryContainer}
                labelSx={styles.summaryText}
                valueSx={styles.summaryValue}
                totalLabelSx={styles.totalText}
                totalValueSx={styles.totalValue}
                hintSx={{ display: 'block', mb: 1, color: 'text.secondary' }}
              />

              {orderData?.shipping && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.75rem', color: '#666' }}
                  >
                    {orderData.shipping.deliveryTime || GROWLER_DELIVERY_TIME}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={styles.footer}>
            <Button
              variant="contained"
              onClick={handleBack}
              sx={styles.advanceButton}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Voltar'
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GrowlerPayment;
